import { useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Loader2, Menu, Shield } from 'lucide-react';
import {
  GRUPOS_DE_FUNCIONALIDAD,
  GRUPOS_DE_MODULOS,
  alMarcar,
  conImplicadas,
  gruposDisponibles,
  type GrupoDePermisos,
} from '../estructura-de-permisos';
import { useTechoParaEditarRoles } from '../hooks/use-techo-para-editar-roles';

/** Los tres estados que puede tener un módulo para un rol. */
export type EstadoDeModulo = 'HABILITADO' | 'BLOQUEADO' | 'OCULTO';

/**
 * En qué estado está un módulo, a partir de las dos listas que guarda la base.
 *
 * Se exporta para que la prueba mida la misma función que dibuja la pantalla,
 * y no una copia de la regla.
 */
export function estadoDelModulo(
  llave: string,
  visibles: string[],
  bloqueados: string[]
): EstadoDeModulo {
  if (!visibles.includes(llave)) return 'OCULTO';
  return bloqueados.includes(llave) ? 'BLOQUEADO' : 'HABILITADO';
}

/** Cuántos módulos de un grupo están en cada estado. */
export function cuentaDelGrupo(
  grupo: GrupoDePermisos,
  visibles: string[],
  bloqueados: string[]
): { habilitados: number; total: number } {
  const estados = grupo.items.map((i) => estadoDelModulo(i.llave, visibles, bloqueados));
  return {
    habilitados: estados.filter((e) => e === 'HABILITADO').length,
    total: grupo.items.length,
  };
}

interface Props {
  abierto: boolean;
  onCerrar: () => void;
  nombreDelRol: string;
  /** Las funcionalidades marcadas. */
  funcionalidades: string[];
  onFuncionalidades: (siguiente: string[]) => void;
  /** Los módulos visibles, y de esos, los que se ven con candado. */
  visibles: string[];
  onVisibles: (siguiente: string[]) => void;
  bloqueados: string[];
  onBloqueados: (siguiente: string[]) => void;
  guardando: boolean;
  onGuardar: () => void;
}

/**
 * Qué puede hacer y qué ve un rol.
 *
 * ⚖️ El orden y los nombres NO se deciden acá: salen de
 * `estructura-de-permisos.ts`, que los declara con los nombres del menú real y
 * tiene un guardián que falla si el backend agrega una llave que nadie ubicó.
 * Antes esta pantalla dibujaba las dos listas del backend en el orden del
 * array, con etiquetas de un mapa aparte que se había desincronizado del menú:
 * decía «Pricing» y «IA Pricing» a cosas que la gente ve como Price
 * Intelligence y Asistente IA.
 *
 * Vive en su propio archivo porque `permissions-manager.tsx` ya tenía 892
 * líneas y este diálogo es la parte que se estaba tocando.
 */
export function DialogoDePermisos({
  abierto,
  onCerrar,
  nombreDelRol,
  funcionalidades,
  onFuncionalidades,
  visibles,
  onVisibles,
  bloqueados,
  onBloqueados,
  guardando,
  onGuardar,
}: Props) {
  /*
    Lo que se dibuja y lo que se guarda, con las implícitas sumadas: un rol
    personalizado llega con sus filas crudas y puede tener «Encender» sin «Ver»
    (el servidor igual le da ver). Al abrir se le avisa al padre, así guardar
    sin tocar nada ya escribe «ver». `conImplicadas` devuelve la misma lista si
    no falta nada: no hay aviso de más ni vuelta infinita.
  */
  const marcadas = useMemo(() => conImplicadas(funcionalidades), [funcionalidades]);
  useEffect(() => {
    if (abierto && marcadas !== funcionalidades) onFuncionalidades(marcadas);
  }, [abierto, marcadas, funcionalidades, onFuncionalidades]);

  // Un producto por contrato que la empresa no tiene no se ofrece (el servidor lo niega igual).
  // El techo es el de la EMPRESA cuyos roles se editan, no el de quien edita (D4, 16-oct): el PLATFORM_ADMIN no tiene.
  const techo = useTechoParaEditarRoles();
  const gruposDeFuncionalidad = useMemo(
    () => gruposDisponibles(GRUPOS_DE_FUNCIONALIDAD, techo),
    [techo]
  );

  const cambiarEstado = (llave: string, siguiente: EstadoDeModulo) => {
    if (siguiente === 'OCULTO') {
      onVisibles(visibles.filter((m) => m !== llave));
      onBloqueados(bloqueados.filter((m) => m !== llave));
      return;
    }
    if (!visibles.includes(llave)) onVisibles([...visibles, llave]);
    if (siguiente === 'BLOQUEADO') {
      if (!bloqueados.includes(llave)) onBloqueados([...bloqueados, llave]);
    } else {
      onBloqueados(bloqueados.filter((m) => m !== llave));
    }
  };

  return (
    <Dialog open={abierto} onOpenChange={(o) => !o && onCerrar()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Permisos de «{nombreDelRol}»</DialogTitle>
          <DialogDescription>Qué puede hacer y qué ve este rol.</DialogDescription>
        </DialogHeader>

        <div className="space-y-8">
          {/* ── Funcionalidad ── */}
          <section>
            <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <Shield className="h-4 w-4" /> Qué puede hacer
            </h4>
            <div className="space-y-4">
              {gruposDeFuncionalidad.map((grupo) => (
                <div key={grupo.titulo} className="rounded-lg border p-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {grupo.titulo}
                  </p>
                  {grupo.nota && (
                    <p className="mb-2 text-xs italic text-muted-foreground">{grupo.nota}</p>
                  )}
                  <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                    {grupo.items.map((item) => (
                      <label
                        key={item.llave}
                        className="flex cursor-pointer items-start gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted/50"
                      >
                        <Checkbox
                          className="mt-0.5"
                          checked={marcadas.includes(item.llave)}
                          onCheckedChange={(marcado) =>
                            onFuncionalidades(alMarcar(marcadas, item.llave, !!marcado))
                          }
                        />
                        <span className="min-w-0">
                          {item.nombre}
                          {item.detalle && (
                            <span className="block text-xs text-muted-foreground">
                              {item.detalle}
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ── Módulos, en el orden del menú ── */}
          <section>
            <h4 className="mb-1 flex items-center gap-2 text-sm font-semibold">
              <Menu className="h-4 w-4" /> Qué ve en el menú
            </h4>
            <p className="mb-3 text-xs text-muted-foreground">
              En el mismo orden en que aparece en la aplicación. <strong>Habilitado</strong>: se ve
              y se usa. <strong>Bloqueado</strong>: se ve con candado pero no se puede usar.{' '}
              <strong>Oculto</strong>: no aparece.
            </p>

            <div className="space-y-4">
              {GRUPOS_DE_MODULOS.map((grupo) => {
                const { habilitados, total } = cuentaDelGrupo(grupo, visibles, bloqueados);
                return (
                  <div key={grupo.titulo} className="rounded-lg border p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {grupo.titulo}
                      </p>
                      {/* De un vistazo: antes había que abrir los 23 desplegables para saberlo. */}
                      <Badge variant={habilitados === 0 ? 'secondary' : 'default'}>
                        {habilitados} de {total} habilitados
                      </Badge>
                    </div>
                    {grupo.nota && (
                      <p className="mb-2 text-xs italic text-muted-foreground">{grupo.nota}</p>
                    )}
                    <div className="space-y-1">
                      {grupo.items.map((item) => (
                        <div
                          key={item.llave}
                          className="flex items-start justify-between gap-3 rounded px-2 py-1.5 text-sm hover:bg-muted/50"
                        >
                          <span className="min-w-0">
                            {item.nombre}
                            {item.detalle && (
                              <span className="block text-xs text-muted-foreground">
                                {item.detalle}
                              </span>
                            )}
                          </span>
                          <select
                            aria-label={item.nombre}
                            value={estadoDelModulo(item.llave, visibles, bloqueados)}
                            onChange={(e) =>
                              cambiarEstado(item.llave, e.target.value as EstadoDeModulo)
                            }
                            className="shrink-0 rounded border border-input bg-background px-1.5 py-1 text-xs"
                          >
                            <option value="HABILITADO">Habilitado</option>
                            <option value="BLOQUEADO">Bloqueado</option>
                            <option value="OCULTO">Oculto</option>
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <div className="flex justify-end gap-3 border-t pt-2">
            <Button variant="outline" onClick={onCerrar} disabled={guardando}>
              Cancelar
            </Button>
            <Button onClick={onGuardar} disabled={guardando}>
              {guardando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Guardar Permisos
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
